import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cx } from '@emotion/css';
import { DSSwitch, DSLinkButton, DSSpinner } from '@stihl-design-system/components';

import { newrelic } from '@phoenix/core/frontend/shared/ui/newrelic/newrelic';
import { link } from '@phoenix/core/frontend/shared/util/navigation/link.utils';
import { PhoenixIcon } from '@phoenix/design-system/frontend/ui/phoenix-icon/phoenix-icon';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { useSearchTools } from '@phoenix/team-m/my-tool-shed/frontend/shared/use-search-tools';
import { InfoMessage } from '@phoenix/team-m/my-tool-shed/frontend/ui/common/info-message';
import { AutomaticSearchLoadingState } from '@phoenix/team-m/my-tool-shed/frontend/ui/overview/automatic-search-loading-state';
import { useUserPreferencesContext } from '@phoenix/team-m/dashboard/frontend/shared/use-user-preferences-context';

import styles from './tool-shed-automatic-search.module.scss';

const noteIcon = getDesignSystemIcon('icon-note-check-16.svg');

interface AutomaticSearchProps {
  className?: string;
}

export const ToolAutomaticSearch = ({ className }: AutomaticSearchProps) => {
  const { 'product-automatic-search': flagEnabled } = useFlags();
  const [checked, setChecked] = useState(false);
  const router = useRouter();
  const { t } = useTranslation();
  const { t: tShared } = useTranslation('shared');
  const { automaticSearchEnabled: searchEnabled, saveUserPreferences, isLoading } = useUserPreferencesContext();
  const { fetchSearchProducts, isSearching, toolsFound, goToSearchResults, resetSearchTools } = useSearchTools(false);

  useEffect(() => {
    if (searchEnabled) {
      setChecked(true);
      !toolsFound?.length && void fetchSearchProducts();
    }
  }, [searchEnabled]);

  const onClickSwitch = () => {
    const toggledValue = !checked;
    setChecked(toggledValue);
    saveUserPreferences({ automaticSearchEnabled: toggledValue });
  };

  const goToAddProduct = (e) => {
    e.preventDefault();
    newrelic.addPageAction('toolShedOverview', {
      context: 'productRegistration',
      triggerType: 'button',
      triggerName: 'addProduct',
      triggerAction: 'click',
    });
    router.push(link(`/tool-shed/add-tool`)).then(resetSearchTools);
  };

  const productCount = searchEnabled ? toolsFound?.length : undefined;

  const addToolBtn = (style?: string, className?: string) => (
    <div className={cx(style, className)}>
      <DSLinkButton
        href={link(`/tool-shed/add-tool`)}
        className="w-full tablet:w-auto"
        iconName="plus"
        data-testid="addToolBtn"
        onClick={goToAddProduct}
      >
        {t('toolShed_overview_button_addTool')}
      </DSLinkButton>
    </div>
  );

  if (!flagEnabled) {
    return addToolBtn('mt-4 justify-end tablet:flex');
  }

  if (isLoading) {
    return <AutomaticSearchLoadingState className={className} />;
  }

  return (
    <section className={className} data-testid="toolAutomaticSearchSection">
      <div className="relative m-auto flex flex-col items-center justify-between desktop:flex-row desktop:pt-12 tabletOnly:max-w-[500px]">
        {addToolBtn(styles.addToolButton, 'desktop:hidden')}
        <DSSwitch
          className="flex w-full items-center !justify-between my-10 desktop:w-auto desktop:my-0"
          onClick={onClickSwitch}
          checked={checked}
          data-testid={`toolAutomaticSearchToggle_${searchEnabled ? 'ON' : 'OFF'}`}
        >
          <span className="block max-w-[330px] pr-6 stihl-text-body-default-reg">{t('addTool_search_automatic_consent_text')}</span>
        </DSSwitch>
        <div className="w-full flex-1 items-center desktop:items-end px-0 tablet:flex flex-col desktop:w-auto desktop:px-12">
          {productCount ? (
            <>
              <div
                aria-live="polite"
                className="left-0 top-2 text-center text-green desktop:absolute"
                data-testid="toolAutomaticSearchProductsFound"
              >
                <PhoenixIcon icon={noteIcon} className="mr-2 inline h-4 w-4" />
                <span className="mr-1 mt-1 stihl-text-h5-desktop">
                  {t('addTool_search_automatic_status_products', { productCount, count: productCount })}
                </span>
              </div>
              <DSLinkButton
                href={link('/tool-shed/add-tool/search-results')}
                aria-live="polite"
                className="w-full mt-8 tablet:w-fit desktop:mt-0"
                iconName="eye"
                onClick={(e) => {
                  e.preventDefault();
                  goToSearchResults();
                }}
                data-testid="toolAutomaticSearchShowBtn"
              >
                {tShared('global_button_showProduct', { count: productCount })}
              </DSLinkButton>
            </>
          ) : isSearching ? (
            <DSSpinner className="m-auto flex w-40 items-center justify-center" />
          ) : (
            <InfoMessage
              aria-live="polite"
              className="items-center justify-center"
              dataTestId={productCount === 0 ? 'toolAutomaticSearchNoProd' : 'toolAutomaticSearchActivate'}
            >
              {t(productCount === 0 ? 'addTool_search_automatic_status_noProducts' : 'addTool_search_automatic_status_activate')}
            </InfoMessage>
          )}
        </div>
        {addToolBtn(styles.addToolButton, 'hidden desktop:flex')}
      </div>
    </section>
  );
};
